import React, { useEffect, useRef, useState } from 'react';
import {
  CAvatar,
  CBadge,
  CButton,
  CDropdown,
  CDropdownDivider,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle
} from '@coreui/react-pro';
import { GearIcon, UserIcon, ClockCounterClockwiseIcon, SignOutIcon, InfoIcon } from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import { API_URL } from 'src/utils/constant';
import LocalStorage from 'src/utils/local_storage';
import defaultAvatar from 'src/assets/default-avatar.png';
import { useTour } from '@reactour/tour';
import { getVisibleSteps } from 'src/utils/steps';
import { useNavigate } from 'react-router-dom';
import { updateSMTPDetails } from 'src/views/customers/actions';
import SMTPConfigModal from './SMTPConfigModal';
import { getRole } from 'src/utils/constant';
import versionInfo from 'src/version.json';
import axios from 'axios';

const LogoutModal = (props) => {
  return (
    <>
      <CModal visible={props.visible} onClose={() => props.setVisible(false)} aria-labelledby='signouModal'>
        <CModalHeader onClose={() => props.setVisible(false)}>
          <CModalTitle className='app-font' id='signouModal'>Sign Out?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className='app-font'>Do you really want to sign out?</p>
        </CModalBody>
        <CModalFooter>
          <CButton className='outline-btn' variant='outline' onClick={() => props.setVisible(false)}>
            Cancel
          </CButton>
          <CButton className='primary-btn' onClick={props.onYes}>
            Yes
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

const AppHeaderDropdown = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userData?.user);
  const [visible, setVisible] = useState(false);
  const { setIsOpen, setSteps, setCurrentStep } = useTour();
  const navigate = useNavigate();

  // Manually launch the tour from the menu. Navigate to the dashboard first so
  // the dashboard cards are mounted, then filter to the steps whose targets are
  // actually on screen (modules/cards are gated by role + licensed services).
  const tourTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(tourTimeoutRef.current), []);
  const startTour = () => {
    navigate('/dashboard');
    clearTimeout(tourTimeoutRef.current);
    tourTimeoutRef.current = setTimeout(() => {
      if (setSteps) setSteps(getVisibleSteps());
      if (setCurrentStep) setCurrentStep(0);
      setIsOpen(true);
    }, 450);
  };


  const logout = async () => {
    // Read OIDC state before clearing storage
    const isOIDCLogin = localStorage.getItem('isOIDCLogin');
    const userEmail = localStorage.getItem('userEmail');
    const token = LocalStorage.getAccessToken();

    // Invalidate the server-side session so the JWT token cannot be replayed
    // after logout (CWE-613 / A02:2025).
    if (token) {
      try {
        await axios.delete(`${API_URL}/logout`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        // Even if the API call fails (e.g. token already expired), proceed with
        // local cleanup — the server-side session will expire naturally.
        console.error('Could not invalidate server session on logout:', e);
      }
    }

    // Clear local storage
    LocalStorage.removeSession();

    if (!!isOIDCLogin && isOIDCLogin !== 'false') {
      try {
        const response = await axios.get(`${API_URL}/oidc/logout${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
          return;
        }
      } catch (e) {
        console.error('Could not get OIDC logout URL');
      }
      window.location.reload();
      return;
    }

    window.location.reload();
  };

  const getProfileImageUrl = () => {
    let imageUrl = '';
    if (user?.profileImage) {
      imageUrl = `${API_URL}${user?.profileImage}`;
    }

    return imageUrl || defaultAvatar;
  };

  return (
    <>
      <CDropdown variant='nav-item' alignment='end' placement='bottom-end'>
        <CDropdownToggle className='py-0' caret={false}>
          {user?.profileImage ?
            <CAvatar src={getProfileImageUrl()} size='md' />
            :
            <CBadge
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                fontWeight: "bold",
              }}
              color="primary"
              shape="rounded-circle"
            >
              {user.fullName?.charAt(0).toUpperCase() || "U"}
            </CBadge>
          }
        </CDropdownToggle>
        <CDropdownMenu className='pt-0 w-100' style={{ minWidth: '220px' }}>
          <div className='px-3 py-2 border-bottom d-flex align-items-center'>
            <div className='bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: '40px', height: '40px' }}>
              {user?.fullName?.charAt(0).toUpperCase()}
            </div>
            <div className='ms-3 text-start'>
              <div className='fw-bold text-nowrap'>{user?.fullName || 'User'}</div>
              <div className='text-muted small'>{getRole(user?.accessLevel)}</div>
            </div>
          </div>
          <CDropdownHeader className=' dark:bg-white dark:bg-opacity-10 fw-semibold py-2'>Settings</CDropdownHeader>
          <CDropdownItem className='custom-dropdown-item' onClick={() => navigate('/profile')}>
            <UserIcon weight="bold" className='me-2' />
            Profile
          </CDropdownItem>
          <CDropdownItem className="custom-dropdown-item" onClick={startTour} hidden={user?.accessLevel === 3 || user?.accessLevel === 2 || user?.accessLevel === 4 ? false : true}>
            <ClockCounterClockwiseIcon weight="bold" className='me-2' />
            Application Tour
          </CDropdownItem>
          <CDropdownItem className='custom-dropdown-item' onClick={() => navigate('/settings')}>
            <GearIcon weight="bold" className='me-2' />
            Settings
          </CDropdownItem>
          <CDropdownDivider />
          <CDropdownItem className="custom-dropdown-item" style={{ pointerEvents: 'none', cursor: 'default' }}>
            <InfoIcon weight="bold" className='me-2' />
            v{versionInfo.version}
          </CDropdownItem>
          <CDropdownDivider />
          <CDropdownItem className="custom-dropdown-item" onClick={() => setVisible(true)}>
            <SignOutIcon weight="bold" className='me-2' />
            Logout
          </CDropdownItem>
        </CDropdownMenu>
        <LogoutModal visible={visible} setVisible={setVisible} onYes={logout} />
      </CDropdown>
    </>
  );
};

export default AppHeaderDropdown;
